/**
 * @file LikeButton.jsx
 * @description A button component to like or unlike an element and view the number of likes.
 * @requires React, PropTypes, semantic-ui-react, react-i18next
 * @exports LikeButton
 */

import React, { useMemo, useContext, useState, useEffect } from 'react';
import PropTypes, { element } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Popup } from 'semantic-ui-react';
import { doc, getDoc, setDoc, updateDoc, increment } from "firebase/firestore";

import { db } from '../../firebaseConfig';
import { EnvironmentContext } from '../../Context/EnvironmentContext';
import './ActionButtons.css';

/**
 * Increments the 'likes' field under stories/{id}/metadata in Firestore.
 * Creates intermediate fields if they do not exist.
 * 
 * @param {string} storyId - The ID of the story document.
 * @returns {Promise<void>}
 * @throws {Error} If `storyId` is not provided or Firestore operations fail.
 * 
 * @example
 * try {
 *    await incrementStoryLikes("story123");
 *    console.log("Likes incremented successfully.");
 * } catch (err) {
 *    console.error("Error incrementing likes:", err);
 * }
 */
export async function incrementStoryLikes(storyId) {
    if (!storyId || typeof storyId !== "string") {
        throw new Error("`storyId` must be a non-empty string.");
    }

    const storyRef = doc(db, "stories", storyId);
  
    try {
        await updateDoc(storyRef, {
            "metadata.likes": increment(1)
        });
    } catch (error) {
        console.error("Failed to increment likes:", error);
        throw error;
    }
}

/**
 * Decrements 'metadata.likes' in stories/{id} if it's > 0.
 * Creates missing fields if needed, but does not allow negatives.
 * 
 * @param {string} storyId - The ID of the story document.
 * @returns {Promise<void>}
 * @throws {Error} If `storyId` is not provided or Firestore operations fail.
 * 
 * @example
 * try {
 *    await decrementStoryLikes("story123");
 *    console.log("Likes decremented successfully.");
 * } catch (err) {
 *    console.error("Error decrementing likes:", err);
 * }
 */
export async function decrementStoryLikes(storyId) {
    if (!storyId || typeof storyId !== "string") {
        throw new Error("`storyId` must be a non-empty string.");
    }

    const storyRef = doc(db, "stories", storyId);
  
    try {
        const snap = await getDoc(storyRef);
    
        if (!snap.exists()) {
            console.warn("Story does not exist.");
            return;
        }
    
        const likes = snap.get("metadata.likes") || 0;
    
        if (likes > 0) {
            await updateDoc(storyRef, {
            "metadata.likes": increment(-1)
            });
        } else {
            console.info("Likes already at 0, no decrement performed.");
        }
    } catch (error) {
        console.error("Failed to safely decrement likes:", error);
        throw error;
    }
}

/**
 * Creates or updates the 'liked' field at users/{ownerId}/history/{id}.
 * Automatically creates the document and parent subcollection if needed.
 * 
 * @param {string} storyId - The ID of the story document.
 * @param {string} ownerId - The ID of the user.
 * @param {boolean} liked - The value to set for the 'liked' field.
 * @returns {Promise<void>}
 */
export async function setUserHistoryLiked(storyId, ownerId, liked) {
    const historyRef = doc(db, "users", ownerId, "history", storyId);
  
    try {
        await setDoc(historyRef, { liked }, { merge: true });
    } catch (error) {
        console.error("Failed to set 'liked' field:", error);
        throw error;
    }
}

/**
 * Checks if a story is liked by a user.
 * 
 * @param {string} ownerId - The ID of the user.
 * @param {string} storyId - The ID of the story (used as the history document ID).
 * @returns {Promise<boolean>} - True if liked, false otherwise.
 */
export async function isStoryLikedByUser(ownerId, storyId) {
    if (!ownerId || !storyId) {
        return false;
    }

    const historyRef = doc(db, "users", ownerId, "history", storyId);
  
    try {
        const docSnap = await getDoc(historyRef);
    
        if (docSnap.exists()) {
            return docSnap.get("liked") === true;
        } else {
            return false;
        }
    } catch (error) {
        console.error("Failed to check if story is liked:", error);
        throw error;
    }
}

/**
 * Retrieves the number of likes from stories/{id}/metadata.likes.
 * 
 * @param {string} storyId - The ID of the story document.
 * @returns {Promise<number>} - The number of likes (0 if missing).
 */
export async function getStoryLikes(storyId) {
    const storyRef = doc(db, "stories", storyId);
  
    try {
        const docSnap = await getDoc(storyRef);
    
        if (docSnap.exists()) {
            const likes = docSnap.get("metadata.likes");
            return typeof likes === "number" ? likes : 0;
        } else {
            console.warn("Story not found:", storyId);
            return 0;
        }
    } catch (error) {
        console.error("Failed to get story likes:", error);
        throw error;
    }
}

/**
 * @typedef {Object} LikeButtonProps
 * @property {string} elementId - Identifier of the element to like or unlike.
 * @property {string} [labelLiked] - Aria label when the element is liked.
 * @property {string} [labelUnliked] - Aria label when the element is unliked. Defaults to localized "Default_AriaLabelUnlike" text.
 * @property {string} [tooltipText] - Tooltip text displayed on hover. Defaults to localized "Popup_Like" text.
 * @property {string} [color='blue'] - Color theme of the button.
 * @property {string} [iconLiked='heart'] - Icon displayed when the element is liked.
 * @property {string} [iconUnliked='heart outline'] - Icon displayed when the element is unliked.
 */

/**
 * LikeButton is a reusable component specialized for Like tasks.
 * 
 * @component
 * @param {ActionButtonProps} props - Component properties.
 * @returns {JSX.Element} A styled button component with an action and tooltip for liking or unliking elements.
 */
const LikeButton = ({
    elementId,
    color='blue',
    labelLiked='Default_AriaLabelLike',
    labelUnliked='Default_AriaLabelUnlike',
    tooltipText='Popup_Like',
    iconLiked='heart',
    iconUnliked='heart outline'
}) => {
    if (!elementId) {
        throw new Error("LikeButton: Missing required prop 'elementId'. Expected a string.");
    }

    const { t } = useTranslation();
    const env = useContext(EnvironmentContext);

    const computedAriaLikedLabel = useMemo(() => labelLiked || t('Default_AriaLabelLike'), [labelLiked, t]);
    const computedAriaUnlikedLabel = useMemo(() => labelUnliked || t('Default_AriaLabelUnlike'), [labelUnliked, t]);
    const computedTooltipText = useMemo(() => tooltipText || t('Popup_Like'), [tooltipText, t]);

    const [likes, setLikes] = useState(null); // Initially null
    const [isLiked, setIsLiked] = useState(null); // Initially null

    const renderLikes = (isMounted) => {
        getStoryLikes(elementId)
            .then((val) => {
                if (isMounted) setLikes(val ?? 0);
            })
            .catch(() => {
                if (isMounted) setLikes(0);
            });
    };

    useEffect(() => {

        let isMounted = true;

        renderLikes(isMounted);

        return () => {
            isMounted = false;
        };
        
    }, [elementId]);

    useEffect(() => {
        let isMounted = true;

        isStoryLikedByUser(env.user?.uid, elementId)
            .then((val) => {
                if (isMounted) setIsLiked(val);
            })
            .catch(() => {
                if (isMounted) setIsLiked(false);
            });

        return () => {
            isMounted = false;
        };
    }, [elementId, env.user?.uid]);

    /**
     * Handles the like/unlike action.
     * 
     * @param {Event} event - The click event.
     * @param {string} storyId - The ID of the story.
     * @param {string} ownerId - The ID of the story owner.
     * @returns {Promise<void>}
     * @throws {Error} If Firestore operations fail.
     */
    const handleLike = async (event, storyId, ownerId) => {
        event.stopPropagation(); // Disables the redirection from the div zone

        if (isLiked) {
            await decrementStoryLikes(storyId)
            await setUserHistoryLiked(storyId, ownerId, false);
            setIsLiked(false);
            renderLikes(true);
        } else {
            await incrementStoryLikes(storyId);
            await setUserHistoryLiked(storyId, ownerId, true);
            setIsLiked(true);
            renderLikes(true);
        }
    }

    return (
        <Popup
            trigger={
                <Button
                    className="element-buttons"
                    onClick={async (event) => handleLike(event, elementId, env.user?.uid)}
                    icon
                    color={color}
                    aria-label={isLiked ? computedAriaLikedLabel : computedAriaUnlikedLabel}
                    disabled={!env.user}
                >
                    <Icon
                        name={isLiked ? iconLiked : iconUnliked}
                    />
                    {` ${likes}`}
                </Button>
            }
            content={computedTooltipText}
            on="hover"
        />
    );
};

LikeButton.propTypes = {
    elementId: PropTypes.string.isRequired,
    color: PropTypes.string,
    labelLiked: PropTypes.string,
    labelUnliked: PropTypes.string,
    tooltipText: PropTypes.string,
    icon: PropTypes.string
};

export default LikeButton;